/**
 * Format validators for the Indian-locale form fields used across the
 * panel: mobile, email, PAN, GST.
 *
 * Each validator returns null on a valid (or empty) input, and a short
 * error string otherwise. Callers should:
 *  - Pass them to a form's `validate` callback OR
 *  - Wire them to an `onBlur` handler that, on failure, clears the
 *    field and pops a toast — that's the "blank text box and show
 *    alert" behaviour the user requested.
 */

const PHONE_RE = /^(\+?\d{1,3}[-\s]?)?\d{10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/; // 10 chars, e.g. AAAPL1234C
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/; // 15 chars
const AADHAAR_RE = /^\d{12}$/;
// Accepted clock formats:
//   "HH:MM"        – 24-hour (browser <input type="time"> default)
//   "HH:MM:SS"     – TimeSpan style (live API on Attendance.checkIn)
//   "h:MM AM/PM"   – legacy free-text entry (we'll convert on save)
//   "hh:MM AM/PM"
const TIME_24H_RE = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
const TIME_12H_RE = /^(0?[1-9]|1[0-2]):([0-5]\d)\s?([AaPp][Mm])$/;

export function validateMobile(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!PHONE_RE.test(trimmed.replace(/\s+/g, ' '))) {
    return 'Mobile must be 10 digits, optionally prefixed with country code';
  }
  return null;
}

/**
 * Strip non-digit characters and clamp to a sensible upper bound.
 *
 * Wire this to a phone `Input`'s `onChange` so users physically
 * cannot type alphabets — pasting a formatted number ("+91 98765
 * 43210") still works, it just normalises to "919876543210".
 *
 * Bug ref: AI Agent Issue #2 (phone field accepts alphabets).
 *
 *   <Input
 *     value={phone}
 *     onChange={(e) => setPhone(sanitizePhone(e.target.value))}
 *     inputMode="numeric"
 *   />
 */
export function sanitizePhone(input: string, maxLen = 15): string {
  return input.replace(/\D+/g, '').slice(0, maxLen);
}

/**
 * Validate a human age. Returns null on a valid (or empty) input,
 * else an error string. Constraints:
 *   - integer digits only (the input boundary should strip non-digits
 *     using `sanitizeAge`)
 *   - range `[0, 120]`
 *
 * Bug ref: AI Agent — "Phone/mobile fields should accept numbers
 * only with proper validation for phone, mobile, and age fields."
 */
export function validateAge(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return 'Age must be a whole number';
  const n = parseInt(trimmed, 10);
  if (n < 0) return 'Age cannot be negative';
  if (n > 120) return 'Age looks too large';
  return null;
}

/** Strip non-digits and clamp to 3 chars (age fits in 3 digits). */
export function sanitizeAge(input: string): string {
  return input.replace(/\D+/g, '').slice(0, 3);
}

export function validateEmail(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!EMAIL_RE.test(value.trim())) {
    return 'Email is not a valid address (e.g. user@company.com)';
  }
  return null;
}

export function validatePan(value: string | null | undefined): string | null {
  if (!value) return null;
  // Auto-uppercase before regex check — PAN is canonically uppercase.
  if (!PAN_RE.test(value.trim().toUpperCase())) {
    return 'PAN must be 10 chars: 5 letters + 4 digits + 1 letter (e.g. AAAPL1234C)';
  }
  return null;
}

export function validateGst(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!GST_RE.test(value.trim().toUpperCase())) {
    return 'GST must be 15 chars (e.g. 27AAAPL1234C1ZA)';
  }
  return null;
}

export function validateAadhaar(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!AADHAAR_RE.test(value.trim().replace(/\s/g, ''))) {
    return 'Aadhaar must be 12 digits';
  }
  return null;
}

/**
 * Validate a wall-clock time. Returns `null` if the input is empty
 * or matches one of the accepted formats:
 *   - 24-hour `HH:MM` (e.g. `09:00`, `23:59`) — what `<input type="time">` produces
 *   - 24-hour `HH:MM:SS` (e.g. `09:00:00`) — what the live API returns for TimeSpan columns
 *   - 12-hour with AM/PM (e.g. `9:00 AM`, `09:00 AM`, `1:30 PM`) — legacy free-text input
 */
export function validateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (TIME_24H_RE.test(v)) return null;
  if (TIME_12H_RE.test(v)) return null;
  return 'Time must be HH:MM (24-hour) or h:MM AM/PM';
}

/**
 * Normalize a wall-clock value to the live API's TimeSpan format
 * `HH:MM:SS`. Returns the original string when it doesn't match a
 * known format — pair with `validateTime()` upstream so junk never
 * reaches this helper.
 *
 *   "09:00"     → "09:00:00"
 *   "09:00:00"  → "09:00:00"
 *   "9:00 AM"   → "09:00:00"
 *   "1:30 PM"   → "13:30:00"
 *   "12:00 AM"  → "00:00:00"
 *   "12:00 PM"  → "12:00:00"
 */
export function toTimeSpan(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (!v) return undefined;
  // 24-hour, with or without seconds.
  const m24 = TIME_24H_RE.exec(v);
  if (m24) {
    const hh = m24[1];
    const mm = m24[2];
    const ss = m24[4] ?? '00';
    return `${hh}:${mm}:${ss}`;
  }
  // 12-hour with AM/PM.
  const m12 = TIME_12H_RE.exec(v);
  if (m12) {
    let hh = parseInt(m12[1], 10);
    const mm = m12[2];
    const ampm = m12[3].toUpperCase();
    if (ampm === 'AM') {
      if (hh === 12) hh = 0;
    } else {
      if (hh !== 12) hh += 12;
    }
    return `${String(hh).padStart(2, '0')}:${mm}:00`;
  }
  return undefined;
}

/**
 * Inverse — strip seconds from a TimeSpan so `<input type="time">`
 * (which only accepts `HH:MM`) can render it.
 *
 *   "09:00:00" → "09:00"
 *   "09:00"    → "09:00"
 *   ""         → ""
 */
export function fromTimeSpan(value: string | null | undefined): string {
  if (!value) return '';
  const v = value.trim();
  return v.length >= 5 ? v.slice(0, 5) : v;
}

export type FieldValidator = (value: string | null | undefined) => string | null;

/**
 * Build an `onBlur` handler that clears the input + shows an alert
 * when the value fails the validator. Pairs with the user's stated
 * UX: "if not valid entry then blank text box and show alert".
 *
 *   onBlur={makeBlurValidator(validateMobile, 'Mobile', toast.error,
 *           (v) => set('phone', v))}
 */
export function makeBlurValidator(
  validate: FieldValidator,
  fieldLabel: string,
  showError: (title: string, body?: string) => void,
  clearValue: (next: string) => void,
): (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void {
  return (e) => {
    const v = e.currentTarget.value;
    const err = validate(v);
    if (err) {
      showError(`Invalid ${fieldLabel}`, err);
      clearValue('');
    }
  };
}
